import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { CheckCircle2, Clock, Sparkles, Zap, TrendingUp, Shield } from 'lucide-react';
import axios from 'axios';
import AuthHeader from '../components/AuthHeader';
import AuthFooter from '../components/AuthFooter';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const TrialActivation = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [hasExistingSubscription, setHasExistingSubscription] = useState(false);

  useEffect(() => {
    checkExistingSubscription();
  }, []);

  const checkExistingSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/subscriptions/current`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.subscription) {
        setHasExistingSubscription(true);
        toast.info('You already have an active subscription');
        navigate('/dashboard');
      }
    } catch (error) {
      // No existing subscription, can proceed with trial
      console.log('No existing subscription');
    } finally {
      setChecking(false);
    }
  };

  const handleActivateTrial = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/subscriptions/trial/activate`,
        { accept_terms: true },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        toast.success('🎉 Trial activated! Complete onboarding to get started.');
        // Redirect to onboarding
        navigate('/agency-onboarding');
      }
    } catch (error) {
      console.error('Trial activation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to activate trial');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking your account...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
      <AuthHeader />
      <div className="flex-1 py-12 px-4 pt-24">
        <div className="max-w-5xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-full mb-4">
            <Sparkles className="w-5 h-5" />
            <span className="font-semibold">Start Your 3-Day Free Trial</span>
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Experience Full Platform Access
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            No credit card required. Get instant access to all features for 3 days. 
            See how AutomateSEO.ai can transform your digital presence.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card>
            <CardHeader>
              <Zap className="w-10 h-10 text-blue-600 mb-2" />
              <CardTitle>Full Platform Access</CardTitle>
              <CardDescription>
                Access all features including AI agents, automation, and analytics
              </CardDescription>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader>
              <TrendingUp className="w-10 h-10 text-green-600 mb-2" />
              <CardTitle>20 AI Credits</CardTitle>
              <CardDescription>
                Generate 20 AI images and 5 videos during your trial period
              </CardDescription>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader>
              <Shield className="w-10 h-10 text-purple-600 mb-2" />
              <CardTitle>No Commitment</CardTitle>
              <CardDescription>
                Cancel anytime. No credit card required. Zero risk.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>

        {/* Main CTA Card */}
        <Card className="border-2 border-blue-200 shadow-xl">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
            <CardTitle className="text-2xl flex items-center gap-2">
              <Clock className="w-6 h-6" />
              3-Day Free Trial
            </CardTitle>
            <CardDescription className="text-base">
              Start your journey to automated SEO and social media success
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4 mb-6">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-900">1 Brand Setup</p>
                  <p className="text-sm text-gray-600">Create and manage your first brand</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-900">10 Social Posts Per Week</p>
                  <p className="text-sm text-gray-600">AI-generated content for your channels</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-900">2 Blogs Per Week</p>
                  <p className="text-sm text-gray-600">SEO-optimized long-form content</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-900">Full Automation Features</p>
                  <p className="text-sm text-gray-600">SEO audits, content generation, and scheduling</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-900">2 Team Members</p>
                  <p className="text-sm text-gray-600">Collaborate with your team</p>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-2">
                <Clock className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900">Trial Duration: Exactly 72 Hours</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Your trial starts the moment you activate. You'll receive reminders 24 hours 
                    before it expires and can upgrade anytime to continue without interruption.
                  </p>
                </div>
              </div>
            </div>

            <Button
              onClick={handleActivateTrial}
              disabled={loading || hasExistingSubscription}
              className="w-full h-12 text-lg"
              size="lg"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Activating Trial...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Start My Free Trial
                </>
              )}
            </Button>

            <p className="text-center text-sm text-gray-500 mt-4">
              By activating the trial, you agree to our Terms of Service and Privacy Policy
            </p>

            <div className="text-center mt-6">
              <button
                onClick={() => navigate('/subscriptions')}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Or view paid plans →
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Trust Section */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500 mb-4">Trusted by businesses worldwide</p>
          <div className="flex justify-center items-center gap-4 text-gray-400">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              <span className="text-sm">No Credit Card Required</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              <span className="text-sm">Cancel Anytime</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              <span className="text-sm">Instant Access</span>
            </div>
          </div>
        </div>
      </div>
      </div>
      <AuthFooter />
    </div>
  );
};

export default TrialActivation;
